// MyMedia.js — Pinterest masonry style, full-width layout (no cut), readable footer metrics
// ✅ Updates per request:
//  1) Masonry (Pinterest-like) using CSS columns (column-width auto-fits to viewport)
//  2) Smaller card sizes (typography/paddings/gaps)
//  3) Sort by creation date DESC (creationDate/created_at fallback)
//  4) More irregular heights (keeps real aspect ratio, no portrait normalization)
//  5) Removed location information from cards
//
// ⚠️ Business logic preserved: fetch/cache/filter/upload handlers kept

import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useDeferredValue,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { Link, useLocation } from "react-router-dom";

const MAX_IMAGE_MB = 10;
const MAX_VIDEO_MB = 200;
const SUCCESS_TOAST_MS = 4000;

const CACHE_TTL_MS = 3 * 60 * 1000;

// ✅ Module-scope cache
const MYMEDIA_CACHE = {
  personal: { data: null, ts: 0 },
  project: { data: null, ts: 0 },
};

// ✅ Users cache (for creator display)
const USERS_CACHE = { map: null, ts: 0 };
const USERS_CACHE_TTL_MS = 10 * 60 * 1000;

// ✅ Module-scope aspect ratio cache (reduce layout shift)
const MEDIA_AR_CACHE = new Map();


// ✅ Multi-language (same logic as LandingPage + SettingsPage)
// SettingsPage stores language in localStorage under "weavevo_lang" (fallback "weavevo_language")
const MYMEDIA_LANG_KEY = "weavevo_lang";

const getInitialLang = () => {
  try {
    const raw =
      localStorage.getItem(MYMEDIA_LANG_KEY) ||
      localStorage.getItem("weavevo_language") ||
      "";
    const norm = String(raw || "").toLowerCase().slice(0, 2);
    return norm === "fr" ? "fr" : "en"; // default EN
  } catch {
    return "en";
  }
};

const MYMEDIA_I18N = {
  en: {
    // Toasts / common
    toast_added: "Media added successfully.",
    close: "Close",

    // Filters / search
    chip_all: "All",
    chip_videos: "Videos",
    chip_images: "Images",
    chip_project: "In a weave",
    chip_personal: "Ready To weave",
    search_aria: "Search",
    search_placeholder: "Search (title, creator)…",

    // Buttons / loading
    btn_create_media: "Create media",
    loading: "Loading…",
    loading_media: "Loading media…",

    // Empty state
    empty_title: "No media",
    empty_sub: "No results for your current filters.",

    // Creator / tooltips
    creator_profile: "Creator profile",
    creator: "Creator",

    // Validation / errors
    err_file_required: "At least one file is required.",
    err_type_unsupported: "Unsupported type (image/ or video).",
    err_image_too_big: "Image too large (> {max} MB).",
    err_video_too_big: "Video too large (> {max} MB).",
    err_location_required: "Location is required.",
    err_upload_failed: "Upload failed.",
    err_network_retry: "Network error. Please try again.",

    // Relative time
    since_just_now: "just now",
    since_in: "in {n} {unit}",
    since_since: "since {n} {unit}",
  },

  fr: {
    // Toasts / common
    toast_added: "Médias ajoutés avec succès.",
    close: "Fermer",

    // Filters / search
    chip_all: "Tous",
    chip_videos: "Vidéos",
    chip_images: "Images",
    chip_project: "Dans un weave",
    chip_personal: "Prêt à weaver",
    search_aria: "Rechercher",
    search_placeholder: "Rechercher (titre, créateur)…",

    // Buttons / loading
    btn_create_media: "Créer un média",
    loading: "Chargement…",
    loading_media: "Chargement des médias…",

    // Empty state
    empty_title: "Aucun média",
    empty_sub: "Aucun résultat pour vos filtres actuels.",

    // Creator / tooltips
    creator_profile: "Profil du créateur",
    creator: "Créateur",

    // Validation / errors
    err_file_required: "Au moins un fichier est requis.",
    err_type_unsupported: "Type non supporté (image/ ou vidéo).",
    err_image_too_big: "Image trop lourde (> {max} MB).",
    err_video_too_big: "Vidéo trop lourde (> {max} MB).",
    err_location_required: "Le lieu est requis.",
    err_upload_failed: "Erreur lors de l'upload.",
    err_network_retry: "Erreur réseau. Veuillez réessayer.",

    // Relative time
    since_just_now: "à l’instant",
    since_in: "dans {n} {unit}",
    since_since: "depuis {n} {unit}",
  },
};


/**
 * LazyMediaVisual:
 * - Lazy loads image/video when near viewport
 * - Uses real media aspect ratio (NO CROP)
 */
function LazyMediaVisual({ isImage, isVideo, src, title }) {
  const holderRef = useRef(null);
  const videoRef = useRef(null);

  const playOnHover = useCallback(() => {
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v) return;
    try {
      v.muted = true;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } catch {}
  }, [isVideo]);

  const stopOnLeave = useCallback(() => {
    if (!isVideo) return;
    const v = videoRef.current;
    if (!v) return;
    try {
      v.pause();
      // Keep a meaningful preview frame when leaving hover
      if (typeof v.currentTime === "number") {
        v.currentTime = Math.min(0.1, v.duration || 0.1);
      }
    } catch {}
  }, [isVideo]);

  const [shouldLoad, setShouldLoad] = useState(false);

  const initialAr = MEDIA_AR_CACHE.get(src) || (isVideo ? 1.25 : 1.35);
  const [aspectRatio, setAspectRatio] = useState(initialAr);

  useEffect(() => {
    const el = holderRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e && e.isIntersecting) {
          setShouldLoad(true);
          obs.disconnect();
        }
      },
      { rootMargin: "420px 0px" }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  const commitAspectRatio = (w, h) => {
    const ww = Number(w) || 0;
    const hh = Number(h) || 0;
    if (!ww || !hh) return;

    // ratio = width/height — clamp extremes to avoid disproportion
    const r = clamp(ww / hh, 0.45, 2.8);
    if (Math.abs(r - aspectRatio) < 0.02) return;

    MEDIA_AR_CACHE.set(src, r);
    setAspectRatio(r);
  };

   // ✅ Same as Admin.js: for portrait VIDEOS only, halve tile height by doubling w/h ratio
  const displayAspectRatio = useMemo(() => {
    const r = aspectRatio;
    if (!Number.isFinite(r) || r <= 0) return r;

    // Only apply to videos, and only if portrait (height > width)
    if (isVideo && r < 1) return r * 2;

    return r;
  }, [aspectRatio, isVideo]);

  return (
    <div
      ref={holderRef}
      className="wv-thumbWrap"
      style={{ aspectRatio: `${displayAspectRatio} / 1` }}
      onMouseEnter={playOnHover}
      onMouseLeave={stopOnLeave}
    >
      {!shouldLoad ? (
        <div className="wv-thumb wv-skeleton" />
      ) : isImage ? (
        <img
          src={src}
          alt={title || "media"}
          className="wv-thumb"
          loading="lazy"
          decoding="async"
          style={{ objectFit: "contain", objectPosition: "center" }}
          onLoad={(e) => {
            const img = e.currentTarget;
            commitAspectRatio(img.naturalWidth, img.naturalHeight);
          }}
        />
      ) : (
        <video
          ref={videoRef}
          src={src}
          preload="metadata"
          className="wv-thumb"
          muted
          playsInline
          loop
          style={{ objectFit: "contain", objectPosition: "center" }}
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            commitAspectRatio(v.videoWidth, v.videoHeight);

            // Force an early frame (helps previews for some browsers)
            try {
              if (typeof v?.currentTime === "number") v.currentTime = Math.min(0.1, v.duration || 0.1);
            } catch {
              // ignore
            }
          }}
        />
      )}

      {/* ✅ Type icon overlay (center) */}
      {(isVideo || isImage) ? (
        <div className={`wv-typeIcon ${isVideo ? "is-video" : "is-image"}`} aria-hidden="true">
          <i className={`bi ${isVideo ? "bi-play-circle-fill" : "bi-image"}`} />
        </div>
      ) : null}
    </div>
  );

}

const MyMedia = () => {
  const { token } = useAuth();
  const location = useLocation();

  // ✅ Language (from SettingsPage) + propagation (focus/storage/visibility)
  const [uiLang, setUiLang] = useState(() => getInitialLang());

  useEffect(() => {
    const read = () => {
      try {
        const raw =
          localStorage.getItem(MYMEDIA_LANG_KEY) ||
          localStorage.getItem("weavevo_language") ||
          "";
        const norm = String(raw || "").toLowerCase().slice(0, 2);
        const next = norm === "fr" ? "fr" : "en";
        setUiLang(next);
        if (!raw) localStorage.setItem(MYMEDIA_LANG_KEY, next);
        document.documentElement.lang = next;
      } catch {}
    };

    read();
    window.addEventListener("storage", read);
    window.addEventListener("focus", read);

    const onVis = () => {
      if (document.visibilityState === "visible") read();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      window.removeEventListener("storage", read);
      window.removeEventListener("focus", read);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const t = useCallback(
    (key, vars) => {
      const dict = MYMEDIA_I18N?.[uiLang] || MYMEDIA_I18N?.en || {};
      let out = dict[key] ?? MYMEDIA_I18N?.en?.[key] ?? key;
      if (vars && typeof out === "string") {
        out = out.replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? `{${k}}`));
      }
      return out;
    },
    [uiLang]
  );


  const [media, setMedia] = useState([]);
  const [projectMedia, setProjectMedia] = useState([]);

  // ✅ Users map (for avatars)
  const [usersByUsername, setUsersByUsername] = useState({});

  // ✅ Same as Admin.js: bust cache after profile update
  const [avatarBust, setAvatarBust] = useState(0);

  useEffect(() => {
    const onProfileUpdated = () => setAvatarBust(Date.now());
    window.addEventListener("profile-updated", onProfileUpdated);
    return () => window.removeEventListener("profile-updated", onProfileUpdated);
  }, []);

  // ✅ Loading state for unified loader
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);

  // Upload state (kept)
  const [newMedia, setNewMedia] = useState({
    location: "",
    visibility: "PUBLIC",
    files: [],
  });

  const [showUploadForm, setShowUploadForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [successToast, setSuccessToast] = useState(false);
  const [errorToast, setErrorToast] = useState("");
  const [fieldErrors, setFieldErrors] = useState({ location: "", files: "" });

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTypes, setFilterTypes] = useState(["ALL"]);
  const deferredSearch = useDeferredValue(searchQuery);

  // Scroll/Highlight new cards
  const cardRefs = useRef({});
  const [pendingScrollIds, setPendingScrollIds] = useState([]);

  const isFresh = (ts) => Date.now() - ts < CACHE_TTL_MS;

  // 🔁 Si on arrive depuis CreateMedia avec des IDs à mettre en avant
  useEffect(() => {
    const highlightIds = location.state?.highlightIds;
    if (Array.isArray(highlightIds) && highlightIds.length) {
      setPendingScrollIds(highlightIds);
    }
  }, [location.state]);

  // 🔁 Afficher le toast de succès si on vient de CreateMedia
  useEffect(() => {
    if (location.state?.fromCreateMedia) {
      setSuccessToast(true);
      const timer = setTimeout(() => setSuccessToast(false), SUCCESS_TOAST_MS);
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  // ✅ Initial load with cache + auto refresh on focus/tab return (fix counters)
  useEffect(() => {
    if (!token) return;

    let alive = true;

    const load = async ({ force = false, showSpinner = true } = {}) => {
      if (showSpinner) setIsLoadingInitial(true);

      // Personal
      if (!force && MYMEDIA_CACHE.personal.data && isFresh(MYMEDIA_CACHE.personal.ts)) {
        if (alive) setMedia(MYMEDIA_CACHE.personal.data);
      } else {
        try {
          const res = await fetch("/media/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await res.json();
          const arr = Array.isArray(data) ? data : [];
          if (alive) setMedia(arr);
          MYMEDIA_CACHE.personal = { data: arr, ts: Date.now() };
        } catch {
          if (alive) setMedia([]);
        }
      }

      // Project media
      if (!force && MYMEDIA_CACHE.project.data && isFresh(MYMEDIA_CACHE.project.ts)) {
        if (alive) setProjectMedia(MYMEDIA_CACHE.project.data);
      } else {
        try {
          const res = await fetch("/project-media/me", {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await res.json();
          const arr = Array.isArray(data) ? data : [];
          if (alive) setProjectMedia(arr);
          MYMEDIA_CACHE.project = { data: arr, ts: Date.now() };
        } catch {
          if (alive) setProjectMedia([]);
        }
      }

      if (showSpinner) setIsLoadingInitial(false);
    };

    // First load (can use cache)
    load({ force: false, showSpinner: true });

    // When user comes back to the tab/window: force refresh counts (no spinner)
    const onFocus = () => load({ force: true, showSpinner: false });
    const onVis = () => {
      if (document.visibilityState === "visible") onFocus();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [token, location.key]);

  /** ---------- Validation (kept) ---------- */
  const resetFieldErrors = () => setFieldErrors({ location: "", files: "" });

  const validateFiles = (entries) => {
    if (!entries || entries.length === 0) return t("err_file_required");
    for (const e of entries) {
      const f = e.file;
      const isImg = f.type?.startsWith("image/");
      const isVid = f.type?.startsWith("video/");
      if (!isImg && !isVid) return t("err_type_unsupported");
      const sizeMB = f.size / (1024 * 1024);
      if (isImg && sizeMB > MAX_IMAGE_MB) return t("err_image_too_big", { max: MAX_IMAGE_MB });
      if (isVid && sizeMB > MAX_VIDEO_MB) return t("err_video_too_big", { max: MAX_VIDEO_MB });
    }
    return "";
  };

  const validateForm = () => {
    const errors = { location: "", files: "" };
    let ok = true;

    if (!newMedia.location?.trim()) {
      errors.location = t("err_location_required");
      ok = false;
    }
    const filesErr = validateFiles(newMedia.files);
    if (filesErr) {
      errors.files = filesErr;
      ok = false;
    }

    setFieldErrors(errors);
    return ok;
  };

  /** ---------- File selection & entry editing (kept) ---------- */
  const fileNameSansExt = (name = "") => name.replace(/\.[^/.]+$/, "");

  useEffect(() => {
    return () => {
      newMedia.files.forEach((e) => e.previewURL && URL.revokeObjectURL(e.previewURL));
    };
  }, []);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    newMedia.files.forEach((entry) => entry.previewURL && URL.revokeObjectURL(entry.previewURL));

    const entries = files.map((f) => ({
      file: f,
      title: fileNameSansExt(f.name),
      description: "",
      previewURL: URL.createObjectURL(f),
    }));

    setNewMedia((prev) => ({ ...prev, files: entries }));

    const filesErr = validateFiles(entries);
    setFieldErrors((fe) => ({ ...fe, files: filesErr }));
  };

  const updateEntry = (idx, patch) => {
    setNewMedia((prev) => {
      const copy = [...prev.files];
      copy[idx] = { ...copy[idx], ...patch };
      return { ...prev, files: copy };
    });
  };

  const removeEntry = (idx) => {
    setNewMedia((prev) => {
      const copy = [...prev.files];
      const removed = copy.splice(idx, 1)[0];
      if (removed?.previewURL) URL.revokeObjectURL(removed.previewURL);
      return { ...prev, files: copy };
    });
  };

  /** ---------- Upload (kept) ---------- */
  const handleUploadMedia = async (e) => {
    e.preventDefault();
    resetFieldErrors();
    setErrorToast("");

    if (!validateForm()) return;

    try {
      setIsUploading(true);
      const createdIds = [];

      for (const entry of newMedia.files) {
        const formData = new FormData();
        formData.append("media", entry.file);
        formData.append("title", (entry.title || fileNameSansExt(entry.file?.name) || "").trim());
        formData.append("location", newMedia.location.trim());
        if (entry.description?.trim()) formData.append("description", entry.description.trim());
        formData.append("visibility", newMedia.visibility);

        const res = await fetch("/upload", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        const result = await res.json();
        if (!res.ok) {
          setErrorToast(result?.error || t("err_upload_failed"));
          setIsUploading(false);
          return;
        }

        setMedia((prev) => [result, ...prev]);
        if (result?.id) createdIds.push(result.id);

        // ✅ Update cache immediately
        MYMEDIA_CACHE.personal = {
          data: [result, ...(MYMEDIA_CACHE.personal.data || [])],
          ts: Date.now(),
        };
      }

      newMedia.files.forEach((entry) => entry.previewURL && URL.revokeObjectURL(entry.previewURL));
      setNewMedia({ location: "", visibility: newMedia.visibility, files: [] });

      setSuccessToast(true);
      setTimeout(() => setSuccessToast(false), SUCCESS_TOAST_MS);

      setShowUploadForm(false);
      if (createdIds.length) setPendingScrollIds(createdIds);
    } catch {
      setErrorToast(t("err_network_retry"));
    } finally {
      setIsUploading(false);
    }
  };

  // ✅ Users directory (kept)
  useEffect(() => {
    if (!token) return;

    // Serve from cache if fresh
    if (USERS_CACHE.map && Date.now() - USERS_CACHE.ts < USERS_CACHE_TTL_MS) {
      setUsersByUsername(USERS_CACHE.map);
      return;
    }

    (async () => {
      try {
        const r = await fetch("/users", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;

        const ct = (r.headers.get("content-type") || "").toLowerCase();
        if (!ct.includes("application/json")) return;

        const rows = await r.json();
        const map = {};
        (Array.isArray(rows) ? rows : []).forEach((u) => {
          const key = (u.username || u.user || u.login || u.email || "").trim();
          if (key) map[key] = u;
        });

        USERS_CACHE.map = map;
        USERS_CACHE.ts = Date.now();
        setUsersByUsername(map);
        setAvatarBust(Date.now()); // ✅ same idea as Admin.js
      } catch {
        // silent
      }
    })();
  }, [token]);

  /** ---------- Creator avatar ---------- */
  const appendBust = (url) => {
    if (!url) return url;
    if (!avatarBust) return url;
    return `${url}${url.includes("?") ? "&" : "?"}v=${avatarBust}`;
  };

  const normalizeUploadPath = (p) => {
    let s = String(p || "").trim();
    if (!s) return "";
    if (s.startsWith("http://") || s.startsWith("https://") || s.startsWith("/uploads/")) return s;
    while (s.startsWith("/")) s = s.slice(1);
    return "/uploads/" + s;
  };

  const avatarFor = useCallback(
    (username, labelForFallback = "") => {
      const k = String(username || "").trim();
      const u = k ? usersByUsername[k] : null;

      // ✅ Prefer avatar_url like Admin.js
      const avatarUrl = u?.avatar_url || u?.avatarUrl || u?.avatar || u?.photo_url;
      if (avatarUrl) return appendBust(String(avatarUrl));

      // ✅ Fallback to stored profilepath
      const profilePath = u?.profilepath || u?.profilePath || u?.profile_path;
      if (profilePath) return appendBust(normalizeUploadPath(profilePath));

      // ✅ Last fallback (ALWAYS WORKS) => ui-avatars (Admin.js behavior)
      const fallbackName = labelForFallback || k || "User";
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(
        fallbackName
      )}&background=dee2e6&color=495057`;
    },
    [usersByUsername, avatarBust]
  );

  const resolveUserSlug = useCallback((raw) => {
    const s = String(raw || "").trim();
    if (!s) return "";
    const base = s.includes("@") ? s.split("@")[0] : s;
    return encodeURIComponent(base.trim());
  }, []);

  const resolveUserProfilePath = useCallback(
    (rawUsername) => {
      const slug = resolveUserSlug(rawUsername);
      return slug ? `/users/${slug}` : "";
    },
    [resolveUserSlug]
  );

  const resolveCreatorUsername = useCallback((m) => {
    // project_media: often contributor
    return String(m.project_creator || m.project_owner || m.contributor || m.user || "").trim();
  }, []);

  /** ---------- Scroll to newly created cards ---------- */
  useEffect(() => {
    if (!pendingScrollIds.length) return;
    const timer = setTimeout(() => {
      const id = pendingScrollIds[0];
      const el = cardRefs.current[id];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("wv-justAdded");
        setTimeout(() => el.classList.remove("wv-justAdded"), 2000);
        setPendingScrollIds((prev) => prev.slice(1));
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [media, projectMedia, pendingScrollIds]);

  /** ---------- UI helpers ---------- */
  const loweredSearch = useMemo(() => (deferredSearch || "").toLowerCase(), [deferredSearch]);

  const filterList = useCallback(
    (list) => {
      return (list || []).filter((m) => {
        const title = (m.title || "").toLowerCase();
        const creator = (resolveCreatorUsername(m) || "").toLowerCase();

        const matchSearch =
          !loweredSearch || title.includes(loweredSearch) || creator.includes(loweredSearch);

        const isImg = m.file_path?.match(/\.(jpg|jpeg|png|webp|gif)$/i);
        const isVid = m.file_path?.match(/\.(mp4|mov|avi|mkv|webm)$/i);

        if (filterTypes.includes("IMAGES") && !isImg) return false;
        if (filterTypes.includes("VIDEOS") && !isVid) return false;
        if (filterTypes.includes("PROJECT") && !m.project_id) return false;
        if (filterTypes.includes("PERSONAL") && m.project_id) return false;

        return matchSearch;
      });
    },
    [filterTypes, loweredSearch, resolveCreatorUsername]
  );

  const filteredProjectMedia = useMemo(() => filterList(projectMedia), [projectMedia, filterList]);
  const filteredPersonalMedia = useMemo(() => filterList(media), [media, filterList]);

  // ✅ Sort by creation date (DESC) for Pinterest-like feed
  const toCreatedTs = useCallback((m) => {
    const v =
      m?.creationDate ??
      m?.creation_date ??
      m?.created_at ??
      m?.createdAt ??
      m?.creation_time ??
      m?.eventdate ??
      m?.date;

    if (!v) return 0;
    if (typeof v === "number") return Number.isFinite(v) ? v : 0;

    const parsed = Date.parse(String(v));
    return Number.isFinite(parsed) ? parsed : 0;
  }, []);

  const sortByDateDesc = useCallback(
    (arr) => {
      const copy = Array.isArray(arr) ? [...arr] : [];
      copy.sort((a, b) => {
        const dt = toCreatedTs(b) - toCreatedTs(a);
        if (dt !== 0) return dt;
        const ida = Number(a?.id || 0);
        const idb = Number(b?.id || 0);
        return idb - ida;
      });
      return copy;
    },
    [toCreatedTs]
  );

  // ✅ One single feed (project + personal), sorted DESC
  const combinedSortedMedia = useMemo(
    () => sortByDateDesc([...(filteredProjectMedia || []), ...(filteredPersonalMedia || [])]),
    [filteredProjectMedia, filteredPersonalMedia, sortByDateDesc]
  );

  const toggleFilter = (type) => {
    if (type === "ALL") {
      setFilterTypes(["ALL"]);
      return;
    }

    let updated = [...filterTypes];

    if (["IMAGES", "VIDEOS"].includes(type)) {
      updated = updated.filter((t) => !["IMAGES", "VIDEOS", "ALL"].includes(t));
      updated.push(type);
      setFilterTypes(updated);
      return;
    }

    if (["PROJECT", "PERSONAL"].includes(type)) {
      updated = updated.filter((t) => !["PROJECT", "PERSONAL", "ALL"].includes(t));
      updated.push(type);
      setFilterTypes(updated);
      return;
    }
  };

  const setCardRef = (id) => (el) => {
    if (el) cardRefs.current[id] = el;
  };

  const showInitialSpinner = isLoadingInitial && media.length === 0 && projectMedia.length === 0;



  // ✅ Locale + "since/depuis" date formatter (follows Settings language)
  const locale = useMemo(() => (uiLang === "fr" ? "fr-FR" : "en-US"), [uiLang]);
  const lang = useMemo(() => (uiLang === "fr" ? "fr" : "en"), [uiLang]);

  const formatSince = useCallback(
    (raw) => {
      if (raw == null) return "";

      const ts = new Date(raw).getTime();
      if (!Number.isFinite(ts)) return "";

      const now = Date.now();
      const diffMs = now - ts;
      const isFuture = diffMs < 0;

      const absSec = Math.floor(Math.abs(diffMs) / 1000);
      if (absSec < 5) return t("since_just_now");

      const units = [
        { sec: 31536000, en: ["year", "years"], fr: ["an", "ans"] },
        { sec: 2592000, en: ["month", "months"], fr: ["mois", "mois"] },
        { sec: 604800, en: ["week", "weeks"], fr: ["semaine", "semaines"] },
        { sec: 86400, en: ["day", "days"], fr: ["jour", "jours"] },
        { sec: 3600, en: ["hour", "hours"], fr: ["heure", "heures"] },
        { sec: 60, en: ["minute", "minutes"], fr: ["minute", "minutes"] },
        { sec: 1, en: ["second", "seconds"], fr: ["seconde", "secondes"] },
      ];

      const u = units.find((x) => absSec >= x.sec) || units[units.length - 1];
      const value = Math.max(1, Math.floor(absSec / u.sec));

      const n = value.toLocaleString(locale);
      const label = (lang === "fr" ? u.fr : u.en)[value > 1 ? 1 : 0];

      if (isFuture) return t("since_in", { n, unit: label });
      return t("since_since", { n, unit: label });
    },
    [lang, locale, t]
  );

const styles = `
.mymedia-pin.page-wrap{
  /* ✅ Full-width override (prevents right-side cut) */
  --wv-sidebar-w: 0px;
  --wv-content-pad: 8px;   /* ✅ more usable width */
  --wv-topbar-h: 74px;

  background: #fafafa;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  overflow-x: visible; /* ✅ do NOT clip masonry last column */

  padding-left: var(--wv-content-pad) !important;
  padding-right: var(--wv-content-pad) !important;
}
@media (max-width: 820px){
  .mymedia-pin.page-wrap{
    --wv-content-pad: 6px; /* ✅ more usable width on small screens */
    padding-left: var(--wv-content-pad) !important;
    padding-right: var(--wv-content-pad) !important;
  }
}

.mymedia-pin, .mymedia-pin *, .mymedia-pin *::before, .mymedia-pin *::after{
  box-sizing: border-box;
}

.mymedia-pin .wv-page{
  padding-top: calc(70px + var(--wv-topbar-h) + 16px);
  padding-left: 0;
  padding-right: 0;
  width: 100%;
  max-width: 100%;
  overflow-x: visible;
  min-width: 0;
}

/* ✅ Top bar (FULL WIDTH FIX) */
.mymedia-pin .wv-topbar{
  position: fixed;
  top: 70px;
  z-index: 1000;
  background: rgba(250,250,250,.92);
  backdrop-filter: blur(10px);

  /* ✅ IMPORTANT: add fallback so calc stays valid */
  left: var(--wv-rail-w, 110px);
  right: 0;

  /* ✅ Prevent horizontal overflow (can clip last masonry column) */
  width: auto;
  max-width: none;

  /* ✅ Remove hardcoded side padding that makes it look “not full width” */
  padding: 10px var(--wv-content-pad);

  box-sizing: border-box;

  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  min-width: 0;
}


/* Chips */
.mymedia-pin .wv-chip{
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 999px;
  cursor: pointer;
  user-select: none;
  font-weight: 500;
  font-size: 13px;
  line-height: 1;
  border: 1px solid rgba(15,23,42,.10);
  background: #fff;
  color: #0f172a;
  transition: transform .12s ease, box-shadow .15s ease;
  white-space: nowrap;
}
.mymedia-pin .wv-chip:hover{
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(15,23,42,.08);
}
.mymedia-pin .wv-chipActive{
  background: #111827;
  color: #fff;
  border-color: rgba(17,24,39,.45);
}

/* Search */
.mymedia-pin .wv-search{
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  gap: .55rem;
  padding: 8px 12px;
  border-radius: 999px;
  background: #f3f4f6;
  border: 1px solid rgba(15,23,42,.06);
  min-width: 240px;
  max-width: min(680px, 100%);
  min-width: min(240px, 100%);
  flex: 1 1 320px;
}
.mymedia-pin .wv-search i{ color: #6b7280; }
.mymedia-pin .wv-search input{
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  font-size: 13px;
}

.mymedia-pin .wv-cta{ flex: 0 0 auto; white-space: nowrap; }

/* ✅ Smaller "Créer un média" button */
.mymedia-pin .wv-createBtn{
  font-size: 12px;
  padding: 6px 10px;
}
.mymedia-pin .wv-createBtn i{ font-size: 12px; }

@media (max-width: 820px){
  .mymedia-pin .wv-search{ min-width: 100%; order: 20; }
  .mymedia-pin .wv-cta{ order: 30; margin-left: auto; }
}

/* ✅ Masonry layout (CSS columns) */
.page-wrap.mymedia-pin .wv-grid{
  width: 100%;
  max-width: 100%;

  /* ✅ Force 5 columns on desktop so you get ~5 media “by row” */
  column-count: 5;
  column-gap: 14px;

  /* ✅ avoid any right-side clipping */
  padding-right: 10px;

  overflow-x: visible;
}

/* Responsive: reduce columns on smaller screens */
@media (max-width: 1500px){
  .mymedia-pin .wv-grid{ column-count: 4; }
}
@media (max-width: 1180px){
  .mymedia-pin .wv-grid{ column-count: 3; }
}
@media (max-width: 820px){
  .mymedia-pin .wv-grid{ column-count: 2; column-gap: 12px; padding-right: 8px; }
}
@media (max-width: 520px){
  .mymedia-pin .wv-grid{ column-count: 1; column-gap: 10px; padding-right: 6px; }
}

/* Each item is a masonry “brick” */
.page-wrap.mymedia-pin .wv-item{
  break-inside: avoid;
  page-break-inside: avoid;
  -webkit-column-break-inside: avoid;

  display: inline-block;
  width: 100%;
  margin: 0 0 14px;

  min-width: 0;
  text-decoration: none;
  color: inherit;
}

/* Responsive columns */
/* Card */
.mymedia-pin .wv-card{
  border: 1px solid rgba(15, 23, 42, .08);
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(2, 6, 23, 0.055);
  transition: transform .14s ease, box-shadow .18s ease;
  will-change: transform;
  backface-visibility: hidden;

  min-width: 0;
  width: 100%;

  /* masonry safety */
  break-inside: avoid;
  -webkit-column-break-inside: avoid;
}
.mymedia-pin .wv-card:hover{
  transform: translateY(-1px) translateZ(0);
  box-shadow: 0 10px 26px rgba(2, 6, 23, 0.10);
}

/* Media */
.mymedia-pin .wv-thumb{
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  object-position: center;
  border: 0;
  background: transparent;
}

.mymedia-pin .wv-thumbWrap{
  width: 100%;
  border-radius: inherit;
  overflow: hidden;
  background: #0b0f19; /* ✅ black letterbox */
  position: relative;

  display: flex;
  align-items: center;
  justify-content: center;

  max-height: 420px;
}

@media (max-width: 560px){
  .mymedia-pin .wv-thumbWrap{ max-height: 340px; }
}

/* ✅ Center overlay icon (video / image) */
.mymedia-pin .wv-typeIcon{
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  pointer-events: none;
}
.mymedia-pin .wv-typeIcon i{
  color: rgba(255,255,255,.92);
  text-shadow: 0 14px 34px rgba(0,0,0,.55);
  opacity: .78;
  transform: scale(1);
  transition: opacity .15s ease, transform .15s ease;
  font-size: 46px;
}
.mymedia-pin .wv-typeIcon.is-image i{
  font-size: 38px;
  opacity: .55;
}
.mymedia-pin .wv-card:hover .wv-typeIcon i{
  opacity: .95;
  transform: scale(1.04);
}


.mymedia-pin .wv-thumbWrap img,
.mymedia-pin .wv-thumbWrap video{
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
  object-position: center !important;
  background: transparent !important;
  transform: none !important;
}

.mymedia-pin .wv-skeleton{
  background: linear-gradient(90deg, #0b0f19, #111827, #0b0f19);
  background-size: 200% 100%;
  animation: wvShimmer 1.2s infinite linear;
  height: 100%;
  width: 100%;
  min-height: 160px;
}
@keyframes wvShimmer{
  0%{ background-position: 200% 0; }
  100%{ background-position: -200% 0; }
}

/* Body */
.mymedia-pin .wv-body{
  padding: 10px 10px 9px;
  display: grid;
  gap: 7px;
}

/* Avatar + title + location inline */
.mymedia-pin .wv-titleRow{
  display:flex;
  align-items:center;
  gap: 8px;
  min-width: 0;
  flex-wrap: nowrap;
}
.mymedia-pin .wv-titleRow .wv-title{
  flex: 1 1 auto;
  min-width: 0;
  font-size: 15px;
  line-height: 1.15;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}
.mymedia-pin .wv-titleLoc{
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, .08);
  background: rgba(248, 250, 252, .9);
  color: rgba(15, 23, 42, .70);
  font-weight: 700;
  font-size: 11px;
  white-space: nowrap;
  max-width: clamp(90px, 28%, 170px);
  overflow: hidden;
  min-width: 0;
}
.mymedia-pin .wv-titleLoc span{
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Prevent long location from pushing metrics */
.mymedia-pin .wv-metric-location{
  max-width: clamp(120px, 22vw, 220px);
}
.mymedia-pin .wv-metric-location .wv-metricText{
  display:inline-block;
  max-width: 100%;
  overflow:hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Meta row (❤️ 💬 always visible) */
.mymedia-pin .wv-meta{
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: rgba(15, 23, 42, .68);
  font-size: 11px;
  flex-wrap: nowrap;
}
.mymedia-pin .wv-metaLeft{
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  min-width: 0;
  flex-wrap: nowrap;
  overflow: hidden;
}
.mymedia-pin .wv-metricText{
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mymedia-pin .wv-metaRight{
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex: 0 0 auto;
  margin-left: auto;
}

/* ✅ Date metric: shrink + ellipsis (avoid pushing ❤️💬) */
.mymedia-pin .wv-metricDate{
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}
.mymedia-pin .wv-metricDate .wv-metricText{
  display: inline-block;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mymedia-pin .wv-metric{
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  font-weight: 700;
  color: rgba(15, 23, 42, .70);
  white-space: nowrap;
  max-width: 100%;
}

/* Creator avatar */
.mymedia-pin .wv-creatorAvatar{
  width: 28px;
  height: 28px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255,255,255,.95);
  border: 2px solid rgba(255,255,255,.92);
  box-shadow: 0 10px 22px rgba(15,23,42,.18);
  display:flex;
  align-items:center;
  justify-content:center;
  flex: 0 0 auto;
}
.mymedia-pin .wv-creatorAvatar img{
  width: 100%;
  height: 100%;
  object-fit: cover;
  display:block;
}

/* Highlight just-added */
.mymedia-pin .wv-justAdded{
  outline: 3px solid #198754;
  animation: wvFlashBg 1.8s ease;
}
@keyframes wvFlashBg{
  0%{ box-shadow: 0 0 0 0 rgba(25,135,84,.7); }
  100%{ box-shadow: 0 0 0 16px rgba(25,135,84,0); }
}

/* Toast */
.mymedia-pin .wv-toast{
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 2000;
  padding: 12px 16px;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 90vw;
  font-weight: 700;
  opacity: 0.98;
  color: #fff;
}
.mymedia-pin .wv-toastSuccess{ background: #198754; }
.mymedia-pin .wv-toastError{ background: #dc3545; }

.mymedia-pin .wv-toastClose{
  background: transparent;
  border: 0;
  color: #fff;
  font-size: 1.1rem;
  line-height: 1;
  opacity: 0.9;
  cursor: pointer;
}`;




  const renderCard = (m) => {
    const isImage = !!m.file_path?.match(/\.(jpg|jpeg|png|webp|gif)$/i);
    const isVideo = !!m.file_path?.match(/\.(mp4|mov|avi|mkv|webm)$/i);

    const src = (() => {
      const p = String(m.file_path || "").trim();
      if (!p) return "";
      if (p.startsWith("http://") || p.startsWith("https://")) return p;
      if (p.startsWith("/uploads/")) return p;
      return `/uploads/${p}`;
    })();

    const creatorRaw = resolveCreatorUsername(m);
    const creatorProfile = resolveUserProfilePath(creatorRaw);
    const creatorLabel = creatorRaw
      ? creatorRaw.includes("@")
        ? creatorRaw.split("@")[0]
        : creatorRaw
      : "User";
    const creatorAvatarSrc = avatarFor(creatorRaw, creatorLabel);
    // Project-media ids live in a different table and can collide with personal media ids.
    // Namespace them explicitly so MediaDetail loads the intended source.
    const mediaDetailsPath = m.project_id
      ? `/mediaDetails/pm-${m.id}`
      : `/mediaDetails/${m.id}`;

    return (
      <div key={`${m.project_id ? "pm" : "media"}-${m.id}-${m.file_path}`} className="wv-item" ref={setCardRef(m.id)}>
        <div className="wv-card">
          <Link to={mediaDetailsPath} className="text-decoration-none text-dark">
            <div className="position-relative">

              <LazyMediaVisual isImage={isImage} isVideo={isVideo} src={src} title={m.title} />
            </div>

            <div className="wv-body">
              {/* ✅ Avatar BEFORE title */}
              <div className="wv-titleRow">
                {creatorProfile ? (
                  <Link
                    to={creatorProfile}
                    className="wv-creatorAvatar"
                    onClick={(e) => e.stopPropagation()}
                    title={t("creator_profile")}
                    aria-label={t("creator_profile")}
                  >
                    <img key={avatarBust} src={creatorAvatarSrc} alt={t("creator")} loading="lazy" />
                  </Link>
                ) : (
                  <div className="wv-creatorAvatar" title={t("creator")} aria-label={t("creator")}>
                    <img src={creatorAvatarSrc} alt={t("creator")} loading="lazy" />
                  </div>
                )}

                <h6 className="wv-title" title={m.title}>
                  {m.title}
                </h6>
                {m.location ? (
                  <span className="wv-titleLoc" title={m.location}>
                    <i className="bi bi-geo-alt" />
                    <span>{m.location}</span>
                  </span>
                ) : null}
              </div>

              {/* ✅ One line: location + date + ❤️ + 💬 */}
              <div className="wv-meta">
                <div className="wv-metaLeft">
                  {m.created_at ? (
                    <span
                      className="wv-metric wv-metricDate"
                      title={new Date(m.created_at).toLocaleString(locale)}
                    >
                      <i className="bi bi-calendar-event" />
                      <span className="wv-metricText">{formatSince(m.created_at)}</span>
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

          </Link>
        </div>
      </div>
    );
  };

  // Keep upload-related states/handlers referenced (UI not rendered here)
  // This prevents strict CI lint settings from failing builds on "unused vars".
  void showUploadForm;
  void isUploading;
  void fieldErrors;
  void handleFileChange;
  void updateEntry;
  void removeEntry;
  void handleUploadMedia;
  void setShowUploadForm;
  void setIsUploading;

  return (
    <div className="page-wrap mymedia-pin">
      <style>{styles}</style>

      {/* Toasts */}
      {successToast ? (
        <div className="wv-toast wv-toastSuccess" role="status" aria-live="polite">
          <i className="bi bi-check-circle-fill" />
          <span>{t("toast_added")}</span>
          <button className="wv-toastClose" onClick={() => setSuccessToast(false)} aria-label={t("close")}>
            ×
          </button>
        </div>
      ) : null}

      {errorToast ? (
        <div className="wv-toast wv-toastError" role="status" aria-live="assertive">
          <i className="bi bi-exclamation-octagon-fill" />
          <span>{errorToast}</span>
          <button className="wv-toastClose" onClick={() => setErrorToast("")} aria-label={t("close")}>
            ×
          </button>
        </div>
      ) : null}

      {/* Topbar */}
      <div className="wv-topbar">
        <div
          className={"wv-chip " + (filterTypes.includes("ALL") ? "wv-chipActive" : "")}
          onClick={() => toggleFilter("ALL")}
        >
          <i className="bi bi-grip-horizontal" /> {t("chip_all")}
        </div>
        <div
          className={"wv-chip " + (filterTypes.includes("VIDEOS") ? "wv-chipActive" : "")}
          onClick={() => toggleFilter("VIDEOS")}
        >
          <i className="bi bi-collection-play" /> {t("chip_videos")}
        </div>
        <div
          className={"wv-chip " + (filterTypes.includes("IMAGES") ? "wv-chipActive" : "")}
          onClick={() => toggleFilter("IMAGES")}
        >
          <i className="bi bi-image" /> {t("chip_images")}
        </div>
        <div
          className={"wv-chip " + (filterTypes.includes("PROJECT") ? "wv-chipActive" : "")}
          onClick={() => toggleFilter("PROJECT")}
        >
          <i className="bi bi-folder2-open" /> {t("chip_project")}
        </div>
        <div
          className={"wv-chip " + (filterTypes.includes("PERSONAL") ? "wv-chipActive" : "")}
          onClick={() => toggleFilter("PERSONAL")}
        >
          <i className="bi bi-inbox" /> {t("chip_personal")}
        </div>

        {/* Search (uses existing searchQuery business logic) */}
       

        {/* operation web disbaled by akrem anaya on june 25th
            Web operation mode: hide the "Create media" button without deleting the original code.

        <div className="wv-cta">
         <Link
  to="/create-media"
  className="btn btn-primary btn-sm rounded-pill d-inline-flex align-items-center gap-1 shadow-sm wv-createBtn"
>
  <i className="bi bi-magic" />
  <span>{t("btn_create_media")}</span>
</Link>

        </div>
        */}
      </div>

      {/* Page content */}
      <div className="wv-page">
        {showInitialSpinner ? (
          <div className="d-flex flex-column align-items-center justify-content-center text-muted py-5">
            <div className="spinner-border mb-2" role="status">
              <span className="visually-hidden">{t("loading")}</span>
            </div>
            <small>{t("loading_media")}</small>
          </div>
        ) : (
          <>
            {/* ✅ Single Pinterest feed */}
            {combinedSortedMedia.length > 0 ? (
              <div className="wv-grid" aria-label="Mes médias">
                {combinedSortedMedia.map((m) => renderCard(m))}
              </div>
            ) : null}

            {combinedSortedMedia.length === 0 && !isLoadingInitial ? (
              <div className="text-muted py-5" style={{ textAlign: "center" }}>
                <div style={{ fontWeight: 800, color: "#0f172a" }}>{t("empty_title")}</div>
                <div style={{ marginTop: 6 }}>{t("empty_sub")}</div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default MyMedia;
